import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useRef, useState } from "react";
import { CheckCircle, CircleNotch, Envelope, Lock, WarningCircle } from "@phosphor-icons/react";
import { getOrderByCheckout, claimOrderByCheckout } from "@/lib/checkout.functions";
import { checkEmailHasAccount } from "@/lib/auth.functions";
import { useCart } from "@/lib/ecom/cart";
import { supabase } from "@/integrations/supabase/client";

function EmailReminderBox({ className }: { className?: string }) {
  return (
    <div className={`rounded-2xl bg-foreground/5 px-4 py-4 sm:px-5 sm:py-5 flex items-start gap-3 ${className ?? ""}`}>
      <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-foreground text-background">
        <Envelope className="h-4 w-4" weight="fill" />
      </div>
      <div>
        <div className="font-bold text-sm sm:text-base">Important — check your email and spam folder</div>
        <div className="text-xs sm:text-sm text-muted-foreground mt-1">
          We sent an order confirmation email with your order info. If you find it in spam, click "Move to inbox" so you can keep receiving order updates from us.
        </div>
      </div>
    </div>
  );
}


export const Route = createFileRoute("/checkout/$id_/success")({
  component: SuccessPage,
});

const MAX_ATTEMPTS = 10;
const POLL_INTERVAL_MS = 4000;

type MinimalOrder = {
  id: string;
  order_number: string;
  status: string;
  email: string | null;
  user_id: string | null;
};

function SuccessPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const fetchOrder = useServerFn(getOrderByCheckout);
  const claimFn = useServerFn(claimOrderByCheckout);
  const { clear } = useCart();
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState<"verifying" | "claim" | "claiming" | "found" | "timeout">("verifying");
  const [order, setOrder] = useState<MinimalOrder | null>(null);
  const cancelled = useRef(false);

  useEffect(() => {
    // Order placed — clear the cart so the user sees an empty cart afterwards.
    clear();
    cancelled.current = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const poll = async (n: number) => {
      if (cancelled.current) return;
      setAttempt(n);
      try {
        const res = await fetchOrder({ data: { checkoutId: id } });
        if (cancelled.current) return;
        if (res?.order?.id) {
          setOrder(res.order as MinimalOrder);
          setState("claim");
          return;
        }
      } catch {
        /* ignore, will retry */
      }
      if (n + 1 >= MAX_ATTEMPTS) {
        setState("timeout");
        return;
      }
      timer = setTimeout(() => poll(n + 1), POLL_INTERVAL_MS);
    };

    poll(0);
    return () => {
      cancelled.current = true;
      if (timer) clearTimeout(timer);
    };
  }, [id, fetchOrder, navigate]);

  async function runClaim() {
    if (!order) return;
    setState("claiming");
    try {
      const res = await claimFn({ data: { checkoutId: id } });
      setState("found");
      setTimeout(() => {
        if (!cancelled.current) {
          navigate({ to: "/orders/$id/customize", params: { id: res.orderId } });
        }
      }, 600);
    } catch (e) {
      throw e;
    }
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="w-full max-w-xl text-center">
        {state === "verifying" && (
          <>
            <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-foreground/5">
              <CircleNotch className="h-8 w-8 animate-spin" />
            </div>
            <h1 className="font-display text-3xl mb-3">Confirming your payment</h1>
            <p className="text-sm text-muted-foreground">
              {attempt === 0
                ? "Hang tight — we're verifying your transaction with our servers."
                : `We haven't received final confirmation yet, please wait… (attempt ${attempt + 1} of ${MAX_ATTEMPTS})`}
            </p>
          </>
        )}
        {(state === "claim" || state === "claiming") && order && (
          <ClaimStep
            order={order}
            busy={state === "claiming"}
            onReady={runClaim}
          />
        )}
        {state === "found" && (
          <>
            <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-foreground text-background">
              <CheckCircle className="h-8 w-8" weight="fill" />
            </div>
            <h1 className="font-display text-3xl mb-3">Payment confirmed</h1>
            <p className="text-sm text-muted-foreground">Taking you to design your cards…</p>
          </>
        )}
        {state === "timeout" && (
          <>
            <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10 text-destructive">
              <WarningCircle className="h-8 w-8" weight="fill" />
            </div>
            <h1 className="font-display text-3xl mb-3">Still confirming…</h1>
            <p className="text-sm text-muted-foreground mb-6">
              We haven't received the final confirmation yet. This can take a few extra minutes.
              We'll email you as soon as your order is ready, or you can refresh this page to try again.
            </p>
            <div className="flex items-center justify-center gap-3">
              <button
                onClick={() => {
                  setState("verifying");
                  setAttempt(0);
                  cancelled.current = false;
                  location.reload();
                }}
                className="rounded-xl bg-foreground px-5 py-2.5 text-sm font-bold text-background"
              >
                Try again
              </button>
              <Link to="/" className="text-sm text-muted-foreground hover:text-foreground">
                Back home
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

type Mode = "signup" | "signin";

function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" className={className} aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6.1 29.5 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/>
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 15.6 18.9 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6.1 29.5 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
      <path fill="#4CAF50" d="M24 44c5.4 0 10.3-2.1 14-5.4l-6.5-5.5c-2 1.5-4.6 2.4-7.5 2.4-5.3 0-9.7-3.4-11.3-8L6.1 32.6C9.5 39.2 16.2 44 24 44z"/>
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.2 4.2-4 5.6l6.5 5.5c-.5.4 6.2-4.5 6.2-15.1 0-1.3-.1-2.4-.4-3.5z"/>
    </svg>
  );
}

function ActionNeededBox({ hasAccount, currentStep }: { hasAccount: boolean | null; currentStep: 1 | 2 }) {
  const step1Copy = hasAccount
    ? "You already have a Banana account — just log in below to design your cards."
    : "Create your Banana account (or sign in with Google) so we can save your card designs and track your order.";
  return (
    <div className="mb-8 rounded-2xl bg-foreground p-5 text-left text-background shadow-xl ring-4 ring-foreground/10">
      <div className="flex items-center gap-2 mb-3">
        <WarningCircle className="h-5 w-5 animate-pulse" weight="fill" />
        <span className="text-[11px] font-black uppercase tracking-widest">Important — Action needed</span>
      </div>
      <p className="text-base font-bold leading-snug mb-4">
        Your order isn't finished yet. To ship it, we need you to design your cards.
      </p>
      <ol className="space-y-3">
        <li className={`flex gap-3 rounded-xl p-3 ${currentStep === 1 ? "bg-background/15 ring-1 ring-background/40" : "opacity-70"}`}>
          <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-black ${currentStep === 1 ? "bg-background text-foreground" : "bg-background/20 text-background"}`}>
            {currentStep > 1 ? "✓" : "1"}
          </span>
          <div className="text-sm leading-snug">
            <div className="font-bold mb-0.5">{hasAccount ? "Log in to your account" : "Create your free account"}</div>
            <div className="text-xs text-background/80">{step1Copy}</div>
          </div>
        </li>
        <li className={`flex gap-3 rounded-xl p-3 ${currentStep === 2 ? "bg-background/15 ring-1 ring-background/40" : "opacity-70"}`}>
          <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-black ${currentStep === 2 ? "bg-background text-foreground" : "bg-background/20 text-background"}`}>
            2
          </span>
          <div className="text-sm leading-snug">
            <div className="font-bold mb-0.5">Design your cards in our editor</div>
            <div className="text-xs text-background/80">Pick materials, upload your logo and finalise your artwork. Your order won't be printed until this step is complete.</div>
          </div>
        </li>
      </ol>
    </div>
  );
}

function ClaimStep({
  order,
  busy,
  onReady,
}: {
  order: MinimalOrder;
  busy: boolean;
  onReady: () => Promise<void>;
}) {
  const orderEmail = (order.email ?? "").toLowerCase();
  const [checkingSession, setCheckingSession] = useState(true);
  const [sessionEmail, setSessionEmail] = useState<string | null>(null);
  const [mode, setMode] = useState<Mode>("signup");
  const [hasAccount, setHasAccount] = useState<boolean | null>(null);
  const [password, setPassword] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [working, setWorking] = useState(false);
  const checkAccount = useServerFn(checkEmailHasAccount);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase.auth.getUser();
      if (cancelled) return;
      const email = data.user?.email?.toLowerCase() ?? null;
      setSessionEmail(email);
      setCheckingSession(false);
      // Already logged in with the matching email — claim immediately.
      if (email && email === orderEmail) {
        try {
          await onReady();
        } catch (e) {
          setErr(e instanceof Error ? e.message : "Could not link your order");
        }
        return;
      }
      // Not signed in (or signed in as someone else) — check whether this
      // order's email already has an account so we pick the right flow.
      if (!email && orderEmail) {
        try {
          const res = await checkAccount({ data: { email: orderEmail } });
          if (cancelled) return;
          setHasAccount(res.hasAccount);
          setMode(res.hasAccount ? "signin" : "signup");
        } catch {
          if (!cancelled) setHasAccount(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderEmail]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!orderEmail) {
      setErr("This order has no email attached — please contact support.");
      return;
    }
    if (password.length < 8) {
      setErr("Password must be at least 8 characters.");
      return;
    }
    setErr(null);
    setWorking(true);
    try {
      if (mode === "signup") {
        const { error } = await supabase.auth.signUp({
          email: orderEmail,
          password,
          options: { emailRedirectTo: window.location.origin },
        });
        if (error) {
          // Common: user already exists — fall back to sign-in flow.
          if (/already|registered|exists/i.test(error.message)) {
            setMode("signin");
            setErr("An account with this email already exists. Please sign in.");
            return;
          }
          throw error;
        }
      } else {
        const { error } = await supabase.auth.signInWithPassword({
          email: orderEmail,
          password,
        });
        if (error) throw error;
      }
      await onReady();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Something went wrong");
    } finally {
      setWorking(false);
    }
  }

  async function switchAccount() {
    setErr(null);
    setWorking(true);
    try {
      await supabase.auth.signOut();
      setSessionEmail(null);
      // Re-check whether the order's email has an account so the next screen
      // shows the correct flow.
      if (orderEmail) {
        try {
          const res = await checkAccount({ data: { email: orderEmail } });
          setHasAccount(res.hasAccount);
          setMode(res.hasAccount ? "signin" : "signup");
        } catch {
          setHasAccount(false);
        }
      }
    } finally {
      setWorking(false);
    }
  }

  async function signInWithGoogle() {
    setErr(null);
    setWorking(true);
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: window.location.href,
          queryParams: { prompt: "select_account" },
        },
      });
      if (error) throw error;
      // Browser will navigate away to Google. If it doesn't, we release the
      // spinner so the user can retry.
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Could not start Google sign-in");
      setWorking(false);
    }
  }

  if (checkingSession) {
    return (
      <>
        <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-foreground/5">
          <CircleNotch className="h-8 w-8 animate-spin" />
        </div>
        <h1 className="font-display text-3xl mb-3">Payment received</h1>
        <p className="text-sm text-muted-foreground">One moment…</p>
      </>
    );
  }

  // Logged in but with a different email — offer to switch account.
  if (sessionEmail && sessionEmail !== orderEmail) {
    return (
      <>
        <ActionNeededBox hasAccount={hasAccount} currentStep={1} />
        <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10 text-destructive">
          <WarningCircle className="h-8 w-8" weight="fill" />
        </div>
        <h1 className="font-display text-3xl mb-2">Wrong account</h1>
        <p className="text-sm text-muted-foreground mb-6">
          This order is for <span className="font-bold text-foreground">{orderEmail}</span>, but you logged in with <span className="font-bold text-foreground">{sessionEmail}</span>. Please log in with <span className="font-bold text-foreground">{orderEmail}</span> to see your order.
        </p>
        <button
          onClick={switchAccount}
          disabled={working}
          className="inline-flex items-center justify-center rounded-xl bg-foreground px-5 py-2.5 text-sm font-bold text-background disabled:opacity-50"
        >
          {working ? <CircleNotch className="h-4 w-4 animate-spin" /> : "Log out"}
        </button>
      </>
    );
  }

  // Not logged in yet — need to create account or sign in.
  return (
    <div className="text-left">
      <div className="text-center mb-4">
        <div className="mx-auto mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full bg-foreground text-background">
          <CheckCircle className="h-6 w-6" weight="fill" />
        </div>
        <h1 className="font-display text-2xl mb-1">Payment received</h1>
        <p className="text-xs text-muted-foreground">
          Order <span className="font-mono font-bold text-foreground">{order.order_number}</span> is paid.
        </p>
      </div>

      <ActionNeededBox hasAccount={hasAccount} currentStep={1} />

      <EmailReminderBox className="mb-6" />

      <button
        type="button"
        onClick={signInWithGoogle}
        disabled={working || busy}
        className="w-full inline-flex items-center justify-center gap-2 rounded-xl border border-border bg-background px-5 py-3 text-sm font-bold text-foreground hover:bg-muted disabled:opacity-50 min-h-[3rem] mb-3"
      >
        <GoogleIcon className="h-5 w-5" />
        Continue with Google
      </button>

      <div className="relative my-4">
        <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border" /></div>
        <div className="relative flex justify-center"><span className="bg-background px-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">or with email</span></div>
      </div>

      <form onSubmit={submit} className="space-y-3">
        <label className="block">
          <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Email</span>
          <div className="mt-1 flex items-center gap-2 rounded-xl border border-border bg-muted px-3 py-2.5">
            <Envelope className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm truncate">{orderEmail || "—"}</span>
          </div>
        </label>
        <label className="block">
          <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Password</span>
          <div className="mt-1 flex items-center gap-2 rounded-xl border border-border bg-background px-3 focus-within:ring-2 focus-within:ring-foreground/20">
            <Lock className="h-4 w-4 text-muted-foreground" />
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={mode === "signup" ? "Create a password (8+ chars)" : "Your password"}
              autoComplete={mode === "signup" ? "new-password" : "current-password"}
              className="w-full bg-transparent py-2.5 text-sm outline-none"
              required
              minLength={8}
            />
          </div>
        </label>

        {err && (
          <div className="rounded-xl border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive">
            {err}
          </div>
        )}

        <button
          type="submit"
          disabled={working || busy}
          className="w-full inline-flex items-center justify-center rounded-xl bg-foreground px-5 py-3 text-sm font-bold text-background disabled:opacity-50 min-h-[3rem]"
        >
          {working || busy ? (
            <CircleNotch className="h-5 w-5 animate-spin" />
          ) : mode === "signup" ? (
            "Create account & design cards"
          ) : (
            "Sign in & design cards"
          )}
        </button>

        {hasAccount === false && (
          <div className="text-center text-xs text-muted-foreground pt-1">
            {mode === "signup" ? (
              <>Already have an account?{" "}
                <button type="button" onClick={() => { setMode("signin"); setErr(null); }} className="underline font-bold text-foreground">Sign in</button>
              </>
            ) : (
              <>Don't have an account?{" "}
                <button type="button" onClick={() => { setMode("signup"); setErr(null); }} className="underline font-bold text-foreground">Create one</button>
              </>
            )}
          </div>
        )}
      </form>
    </div>
  );
}
